import type { TerrainShape, Vec2 } from '../model/format.ts';
import { allowedEdge } from '../terrain/index.ts';

const DIRS: Vec2[] = [
  [1, 0], [0, 1], [-1, 0], [0, -1],
  [1, 1], [-1, 1], [1, -1], [-1, -1],
  [2, 1], [-2, 1], [2, -1], [-2, -1],
  [1, 2], [-1, 2], [1, -2], [-1, -2],
];

export function edgeOk(style: string, a: Vec2, b: Vec2): boolean {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  if (!dx && !dy) return false;
  return allowedEdge(style, dx, dy);
}

/** Snap the cursor to the nearest grid point reachable from `from` along an allowed direction. */
export function snapSegment(style: string, from: Vec2, cursor: [number, number]): Vec2 {
  const vx = cursor[0] - from[0], vy = cursor[1] - from[1];
  let best: Vec2 = [Math.round(cursor[0]), Math.round(cursor[1])];
  let bestD = Infinity;
  for (const d of DIRS) {
    if (!allowedEdge(style, d[0], d[1])) continue;
    const t = Math.max(1, Math.round((vx * d[0] + vy * d[1]) / (d[0] * d[0] + d[1] * d[1])));
    const p: Vec2 = [from[0] + d[0] * t, from[1] + d[1] * t];
    const dist = Math.hypot(p[0] - cursor[0], p[1] - cursor[1]);
    if (dist < bestD) {
      bestD = dist;
      best = p;
    }
  }
  return best;
}

export function pointInRing(x: number, y: number, ring: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function pointInTerrain(t: TerrainShape, x: number, y: number): boolean {
  if (!t.rings.length || !pointInRing(x, y, t.rings[0])) return false;
  for (let i = 1; i < t.rings.length; i++) if (pointInRing(x, y, t.rings[i])) return false;
  return true;
}

export function ringBounds(ring: Vec2[]): [number, number, number, number] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of ring) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return [x0, y0, x1, y1];
}

/** Ring edges that the style can't build (for red highlighting). */
export function badEdges(style: string, ring: Vec2[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < ring.length; i++) if (!edgeOk(style, ring[i], ring[(i + 1) % ring.length])) out.push(i);
  return out;
}

/** The ring edge closest to (x, y) within maxDist, with the grid point nearest to (x, y) projected onto it. */
export function nearestEdge(ring: Vec2[], x: number, y: number, maxDist: number): { edge: number; point: Vec2; dist: number } | null {
  let best: { edge: number; point: Vec2; dist: number } | null = null;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const len2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / len2));
    const px = a[0] + t * dx, py = a[1] + t * dy;
    const d = Math.hypot(px - x, py - y);
    if (d < (best?.dist ?? maxDist)) best = { edge: i, point: [Math.round(px), Math.round(py)], dist: d };
  }
  return best;
}
